import { LayoutGrid, List } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Two-button segmented control for Grid / List view toggle.
 * Persists the choice in localStorage under the given storage key.
 */
export default function ViewToggle({ value, onChange, storageKey, testIdPrefix }) {
  return (
    <div className="inline-flex border border-[#27272A]" data-testid={`${testIdPrefix}-view-toggle`}>
      <button
        type="button"
        onClick={() => onChange("list")}
        data-testid={`${testIdPrefix}-view-list`}
        title="List view"
        className={`px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest transition-colors ${
          value === "list" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white hover:bg-[#1a1a1a]"
        }`}
      >
        <List className="size-3" /> List
      </button>
      <button
        type="button"
        onClick={() => onChange("grid")}
        data-testid={`${testIdPrefix}-view-grid`}
        title="Grid view"
        className={`px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest transition-colors border-l border-[#27272A] ${
          value === "grid" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white hover:bg-[#1a1a1a]"
        }`}
      >
        <LayoutGrid className="size-3" /> Grid
      </button>
    </div>
  );
}

/** Hook that returns a [view, setView] pair persisted via localStorage. */
export function usePersistedView(storageKey, fallback = "list") {
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return fallback;
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === "grid" || v === "list" ? v : fallback;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn(`ViewToggle read ${storageKey} failed:`, err);
      return fallback;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, view);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn(`ViewToggle persist ${storageKey} failed:`, err);
    }
  }, [storageKey, view]);
  return [view, setView];
}
